/**
 * useActivationComposerState — le brouillon, l'envoi, et les trois ouvertures restées legacy.
 *
 * Le brouillon vit dans `activationDraftReducer` (pur, testé à part) ; ce crochet le
 * re-synchronise sur l'amorce, expose un geste par section, et porte l'ENVOI : UN seul formulaire,
 * UN seul POST (`ActivationComposerScreen.js:637-699`).
 *
 * L'envoi suit le patron d'écriture des ports : trois gardes (`awaiting`, `handledError`,
 * `handledSuccess`), un `apiIdle` après chaque issue et au démontage, et un message d'erreur
 * normalisé en chaîne — le refus du back porte la raison actionnable quand il en a une.
 *
 * La grille de créneaux, le catalogue de matières et le comparatif des formules restent legacy :
 * le pont les monte et passe ses ouvreurs en props. Les ports lui rendent la configuration et le
 * rappel de validation ; l'état choisi, lui, revient dans le brouillon.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigation } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import { ACTIVATION_SAVE_FAILED_SNACKBAR } from "../../../../adapters/parent/shared/activationDisplay"
import {
    type ActivationComposerModel,
    type ActivationDraft,
    type ActivationSubmission,
} from "../../../../adapters/parent/Activation/activationAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { activationDraftReducer, initialActivationDraftState } from "./activationDraft"
import { type ActivationLinkParams } from "./activationLink"

/**
 * Source de suivi dédiée à l'activation self-service — distincte de celle de la reprise, qui
 * s'adresse à des familles déjà parties (`ActivationComposerScreen.js:51`).
 */
const ACTIVATION_TRACKING_SOURCE = "parent_subscription.self_serve"

type Feedback = { error: string | null; success: string | null }

const NO_FEEDBACK: Feedback = { error: null, success: null }

export type ActivationPickerOpeners = {
    onOpenSlots: (request: ActivationComposerModel["slotsPicker"] & { onValidate: (slots: string[]) => void }) => void
    onOpenSubjects: (
        request: ActivationComposerModel["subjectsPicker"] & { onValidate: (ids: (string | number)[]) => void }
    ) => void
    onOpenOfferInfo: () => void
}

export type UseActivationComposerStateArgs = ActivationPickerOpeners & {
    linkParams: ActivationLinkParams | null
    seed: ActivationDraft
    soldNoCommitmentHours: Record<string, number | null>
    seedKey: string
    isModification: boolean
}

export function useActivationComposerState(args: UseActivationComposerStateArgs) {
    const { linkParams, seed, soldNoCommitmentHours, seedKey, isModification, onOpenSlots, onOpenSubjects } = args
    const dispatch = useDispatch()
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string, params?: Record<string, unknown>) => void
        navigate: (name: string, params?: Record<string, unknown>) => void
    }>()

    const [state, dispatchDraft] = useReducer(activationDraftReducer, initialActivationDraftState)
    const [feedback, setFeedback] = useState<Feedback>(NO_FEEDBACK)

    // La porte décide du service : deux appels distincts pour que chaque entrée ait son propre
    // état de chargement et d'erreur.
    const submitService = linkParams
        ? subscriptionService.activateSubscriptionSuiteFromLink
        : subscriptionService.activateSubscriptionSuite

    /**
     * Les deux portes écrivent sur deux tranches distinctes : celle qu'on n'a pas appelée peut
     * porter un reste d'un montage précédent, donc les deux sont rendues au repos au démontage.
     */
    const pendingTrackingRef = useRef<ActivationSubmission["tracking"] | null>(null)

    const { start: startSubmit } = useApiMutation({
        service: submitService,
        fallback: ACTIVATION_SAVE_FAILED_SNACKBAR,
        // Un second tap pendant le vol ne repart pas : l'envoi est unique par formulaire.
        skipWhilePending: true,
        idleOnUnmount: [
            subscriptionService.activateSubscriptionSuite,
            subscriptionService.activateSubscriptionSuiteFromLink,
        ],
        onSuccess: () => {
            const tracking = pendingTrackingRef.current
            pendingTrackingRef.current = null
            if (tracking) {
                amplitude.track(`${ACTIVATION_TRACKING_SOURCE}.activation_confirmed`, {
                    source: "self_serve",
                    ...tracking,
                })
            }
            // Re-lire l'état chez l'APPELANT avant de revenir : l'arrivée et le portail écoutent
            // chacun leur service, le déclencher d'ici suffit.
            if (linkParams) {
                dispatch(apiRequest(subscriptionService.getActivationLinkContext, {}, {}, linkParams) as never)
            } else {
                dispatch(apiRequest(subscriptionService.getSubscribePricing) as never)
            }
            setFeedback({
                error: null,
                success: isModification ? "Modifications enregistrées." : "La suite est planifiée.",
            })
        },
        onError: (message) => {
            pendingTrackingRef.current = null
            // L'écran reste ouvert — le parent garde sa saisie et peut réessayer. Le refus du back
            // porte la raison actionnable quand il en a une.
            setFeedback({ error: message, success: null })
        },
    })

    /**
     * Re-synchronisation sur l'amorce : la date repart de sa présélection à chaque (ré)ouverture —
     * un choix non confirmé ne survit pas à la fermeture (`ActivationComposerScreen.js:508-522`).
     */
    useEffect(() => {
        dispatchDraft({ type: "SYNC", draft: seed, soldNoCommitmentHours })
        // `seedKey` résume l'amorce : caler l'effet sur les objets le ferait repartir à chaque rendu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedKey])

    const goBack = useCallback(() => {
        // Retour DÉTERMINISTE : après un rechargement à froid, `goBack` n'a plus d'historique.
        if (navigation.canGoBack()) navigation.goBack()
        else if (linkParams) navigation.replace("activation", linkParams)
        else navigation.replace("menu")
    }, [navigation, linkParams])

    const leaveAfterSuccess = useCallback(() => {
        setFeedback(NO_FEEDBACK)
        // L'entrée lien revient sur l'arrivée (mêmes paramètres) ; l'entrée connectée revient d'où
        // elle vient.
        if (linkParams) navigation.navigate("activation", linkParams)
        else if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation, linkParams])

    const submit = useCallback(
        (submission: ActivationSubmission | null) => {
            // Offre non calculable : jamais 0 €, jamais d'envoi.
            if (submission == null) return
            pendingTrackingRef.current = submission.tracking
            if (!startSubmit({}, submission.body, linkParams ?? {})) {
                pendingTrackingRef.current = null
                return
            }
            amplitude.track(`${ACTIVATION_TRACKING_SOURCE}.activation_submitted`, {
                source: "self_serve",
                offer_type: submission.tracking.offer_type,
            })
        },
        [startSubmit, linkParams]
    )

    const openSlots = useCallback(
        (request: ActivationComposerModel["slotsPicker"]) =>
            onOpenSlots({
                ...request,
                onValidate: (slots) => dispatchDraft({ type: "SET_SLOTS", value: slots }),
            }),
        [onOpenSlots]
    )

    const openSubjects = useCallback(
        (request: ActivationComposerModel["subjectsPicker"]) =>
            onOpenSubjects({
                ...request,
                onValidate: (ids) => dispatchDraft({ type: "SET_SUBJECTS", value: ids }),
            }),
        [onOpenSubjects]
    )

    const actions = useMemo(
        () => ({
            goBack,
            submit,
            openSlots,
            openSubjects,
            openOfferInfo: args.onOpenOfferInfo,
            selectClass: (value: string) => dispatchDraft({ type: "SET_CLASS", value }),
            selectHours: (value: number) => dispatchDraft({ type: "SET_HOURS", value }),
            selectOffer: (value: string) => dispatchDraft({ type: "SET_OFFER", value }),
            selectResumeDate: (value: string) => dispatchDraft({ type: "SET_RESUME_DATE", value }),
            changeCommitment: (value: boolean) => dispatchDraft({ type: "SET_COMMITMENT", value }),
            changeAllocation: (subjectId: string | number, hours: number) =>
                dispatchDraft({ type: "SET_ALLOCATION", subjectId, hours }),
            dismissError: () => setFeedback((current) => ({ ...current, error: null })),
            // L'accusé de succès EST la sortie : on quitte quand il se retire, comme le legacy
            // quittait dès que sa SnackBar globale était posée.
            dismissSuccess: leaveAfterSuccess,
        }),
        [goBack, submit, openSlots, openSubjects, args.onOpenOfferInfo, leaveAfterSuccess]
    )

    return { draft: state.draft, feedback, actions }
}
